"use client"

import {observer} from "mobx-react";
import React from "react";
import moment from "moment";
import Calendar from "../utilities/CleverCalendar/CleverCalendar";
import {deleteContentItem} from "../blocks/TerracedGridTheme/Terrace";
import {ISchoolFeedUrlQueries} from "../../utils/SchoolBlocksUtilities";
import {InitialLoadingIndicatorWrapper} from "./InitialLoadingIndicatorWrapper";
import {IReactiveEventContentItem, ReactiveContentItem} from "./SchoolFeedContentItem";
import SafeRouter from "../utilities/SafeRouter";
import useSWRInfinite from "swr/infinite";
import {useSchoolFeedContentPathQueryParams} from "./hooks/useSchoolFeedInfiniteScroll";
import {currentRangeUrlQueryFormat, getEventsForRange} from "./utils";
import {useSchoolFeedUrlQuery} from "../../hooks/useSchoolFeedData";
import {currentDateFormat, ICurrentRange} from "../utilities/CleverCalendar/utils";

export function getCacheKeyFromCurrentRangeUrlQuery(urlQuery: ISchoolFeedUrlQueries) {
    return JSON.stringify({
        start: urlQuery.currentRangeStart,
        end: urlQuery.currentRangeEnd,
        orgId: !urlQuery.orgId,
    });
}



interface DateRange {
    start: moment.Moment;
    end: moment.Moment;
}

const SchoolFeedEventsDisplay: React.FC<{
    urlQuery: ISchoolFeedUrlQueries,
    fallbackData?: Array<EventContentItem>,
}> = observer((props) => {
    const urlQuery = useSchoolFeedUrlQuery();
    const pathQueryParams = useSchoolFeedContentPathQueryParams();

    function useGetEvents () {
        const cacheKey = getCacheKeyFromCurrentRangeUrlQuery(urlQuery);
        const {data: events, isLoading, mutate, error} = useSWRInfinite(() => cacheKey, async (key) => {
            const parsedKey = JSON.parse(key)
            const events = await getEventsForRange(pathQueryParams, urlQuery, parsedKey.start, parsedKey.end);
            return events.map(e => new ReactiveContentItem((e)))
        }, {
            revalidateOnFocus: false,
            revalidateOnMount: !props.fallbackData,
            fallbackData: props.fallbackData ? [props.fallbackData.map(d => new ReactiveContentItem(d))] : undefined,
        });

        const returnEvents = events ? events[0] : []

        return {
            events: returnEvents,
            isLoading,
            mutate,
            error,
        }
    }

    const {events, isLoading, mutate, error} = useGetEvents();

    const handleDelete = async (contentItem) => {
        await deleteContentItem(contentItem);
        mutate();
    }

    const handleRangeUpdate = (range: ICurrentRange) => {
        SafeRouter.setQueries(
            ["currentRangeStart", range.start],
            ["currentRangeEnd", range.end],
        );
    }

    return (
        <InitialLoadingIndicatorWrapper
            initialFetchIsLoading={false}
            dataSize={1}
            errorCode={error?.status}
            showInitialLoadingIndicator={false}
        >
            <Calendar
                events={events as IReactiveEventContentItem[] || []}
                loading={isLoading}
                onRangeChange={handleRangeUpdate}
                aboveTheFold={true}
                expandedView={true}
                handleDeleteEvent={handleDelete}
                initialDate={props.urlQuery.currentRangeStart ? moment(props.urlQuery.currentRangeStart, currentRangeUrlQueryFormat).add(3, 'days').format(currentDateFormat) : undefined}
            />
        </InitialLoadingIndicatorWrapper>
    );
})

export default SchoolFeedEventsDisplay;
